import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import { Eye, ClipboardList, CalendarDays, Gavel, ListOrdered, Settings, Trophy, HeartHandshake, Sprout } from "lucide-react";
import { api } from "@/api";
import Watch from "@/views/Watch";
import Register from "@/views/Register";
import Sponsor from "@/views/Sponsor";
import Draw from "@/views/Draw";
import Judges from "@/views/Judges";
import Results from "@/views/Results";
import Setup from "@/views/Setup";
import Awards from "@/views/Awards";
import ResetPassword from "@/views/ResetPassword";
import Volunteer from "@/views/Volunteer";
import ShirtSize from "@/views/ShirtSize";
import Workshop from "@/views/Workshop";
import AnnouncerScreen from "@/views/AnnouncerScreen";

const NAV = [
  { to: "/", label: "Watch", icon: Eye, testid: "nav-watch", end: true, liveOnly: true },
  { to: "/register", label: "Enter", icon: ClipboardList, testid: "nav-register" },
  { to: "/draw", label: "Draw", icon: CalendarDays, testid: "nav-draw" },
  { to: "/judges", label: "Judges", icon: Gavel, testid: "nav-judges", liveOnly: true },
  { to: "/results", label: "Results", icon: ListOrdered, testid: "nav-results" },
  { to: "/volunteer", label: "Volunteer", icon: HeartHandshake, testid: "nav-volunteer" },
  { to: "/workshop", label: "Garden", icon: Sprout, testid: "nav-workshop" },
  { to: "/setup", label: "Setup", icon: Settings, testid: "nav-setup" },
  { to: "/awards", label: "Awards", icon: Trophy, testid: "nav-awards" },
];

function BottomNav({ eventLive }) {
  const items = NAV.filter((n) => !n.liveOnly || eventLive);
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t-4 border-black flex overflow-x-auto hide-scrollbar" data-testid="bottom-nav">
      {items.map((n) => {
        const Icon = n.icon;
        return (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex-1 shrink-0 basis-[64px] min-w-[64px] sm:min-w-0 flex flex-col items-center justify-center py-2 font-display font-bold uppercase text-[10px] leading-none tracking-tight whitespace-nowrap transition-colors ${
                isActive ? "bg-black text-white" : "text-black"
              }`
            }
          >
            <Icon size={20} className="mb-1 shrink-0" />
            {n.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function LiveBanner() {
  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-[#E63946] text-white overflow-hidden" data-testid="live-banner">
      <div className="flex items-center justify-center gap-2 py-1.5 px-4">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
        <span className="font-display font-black uppercase tracking-[0.3em] text-xs sm:text-sm">
          Live Now — Pounders Fest is running
        </span>
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
      </div>
    </div>
  );
}

function Shell({ eventLive }) {
  const location = useLocation();
  const bare = location.pathname === "/announcer";
  return (
    <div className="App min-h-screen" style={eventLive && !bare ? { paddingTop: 32 } : undefined}>
      {eventLive && !bare && <LiveBanner />}
      <Routes>
        <Route path="/" element={<Watch />} />
        <Route path="/register" element={<Register />} />
        <Route path="/sponsor" element={<Sponsor />} />
        <Route path="/draw" element={<Draw />} />
        <Route path="/judges" element={<Judges />} />
        <Route path="/results" element={<Results />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/awards" element={<Awards />} />
        <Route path="/volunteer" element={<Volunteer />} />
        <Route path="/workshop" element={<Workshop />} />
        <Route path="/shirt" element={<ShirtSize />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/announcer" element={<AnnouncerScreen />} />
      </Routes>
      {!bare && <BottomNav eventLive={eventLive} />}
    </div>
  );
}

function App() {
  const [eventLive, setEventLive] = useState(false);
  useEffect(() => {
    const load = () => api.config().then((c) => setEventLive(!!c.event_live)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  return (
    <BrowserRouter>
      <Shell eventLive={eventLive} />
    </BrowserRouter>
  );
}

export default App;
